import { Signal } from '@crown/signal';
import { isJsonObject, parseJson } from '../ir/json';
import { activeRegistry, isShown, type Resource, resource } from './resource';
import { hasGlobal } from '../ir/guards';

/**
 * Route data, Remix style: a page route's `loader` runs on the server, and
 * what it returned is filed under `['route', id, path]`. `routeData(id)` is a
 * resource on that key, so a server-rendered page reads it without fetching;
 * after a client navigation the key's path changes, and it fetches
 * `path?_data=id`, which the page server answers with that loader's JSON under
 * the caller's own permissions. A route whose path did not change keeps its data.
 */

/** The page as route data keys it: pathname and query, without `_data`. */
export function dataPath(url: URL | string): string {
  const parsed = url instanceof URL ? url : new URL(url, 'http://localhost');
  const params = new URLSearchParams(parsed.search);
  params.delete('_data');
  const query = params.toString();
  return query ? `${parsed.pathname}?${query}` : parsed.pathname;
}

/** Where the browser is, as route data sees it; a router moves it on navigation. */
const location = new Signal.State<string>(hasGlobal('window') ? dataPath(window.location.href) : '/');

/** Tells route data the page moved, for a navigation the history does not see. */
export function setRoutePath(url: URL | string): void {
  location.set(dataPath(url));
}

let following = false;

/**
 * Follows the browser's history, whichever router moves it: back and forward
 * arrive as `popstate`, and `pushState`/`replaceState` are observed where they
 * are called. Installed once, by the first route data a page reads.
 */
function followHistory(): void {
  if (following || !(hasGlobal('window')) || !window.history) return;
  following = true;
  const sync = (): void => setRoutePath(window.location.href);
  // Where the page is now, which may have moved since this module loaded.
  sync();
  window.addEventListener('popstate', sync);
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = window.history[method];
    window.history[method] = function (this: History, ...args: Parameters<History['pushState']>) {
      original.apply(this, args);
      // The address as the call gave it, resolved against the page.
      const [, , url] = args;
      if (url === undefined || url === null) sync();
      else setRoutePath(new URL(String(url), window.location.href));
    };
  }
}

/** The key a route's data is filed under. */
export function routeDataKey(id: string, path: string): readonly [string, string, string] {
  return ['route', id, path];
}

function currentPath(): string {
  return activeRegistry().path ?? location.get();
}

/** The URL that asks the page server for some routes' data. */
export function routeDataUrl(path: string, ids: readonly string[]): string {
  return `${path}${path.includes('?') ? '&' : '?'}_data=${ids.map(encodeURIComponent).join(',')}`;
}

// Held weakly: route data from a page no longer shown can be collected.
const live = new Set<WeakRef<Resource<unknown>>>();

/** A route's loader data, as a resource: read on the server as the loader returned it, fetched after a navigation. */
export function routeData<T>(id: string): Resource<T> {
  followHistory();
  const data = resource(
    () => routeDataKey(id, currentPath()),
    async ([, route, path], { signal }) => {
      const response = await fetch(routeDataUrl(path, [route]), { signal, headers: { accept: 'application/json' } });
      if (!response.ok) throw new Error(`The data for route ${route} failed (${response.status})`);
      const parsed = parseJson(await response.text());
      if (!parsed.ok) throw new Error(parsed.reason);
      const body = parsed.value;
      if (!isJsonObject(body) || !(route in body)) throw new Error(`The page server sent no data for route ${route}`);
      // SAFETY: T is the caller's word for what this route's loader returns,
      // and this is that loader's JSON as the page server sent it.
      return body[route] as T;
    },
  );
  live.add(new WeakRef(data));

  return data;
}

/** Fetches the data of every route on the page again, after a change that might have altered it (a form was saved, say). */
export function revalidate(): void {
  for (const ref of live) {
    const data = ref.deref();
    if (!data) live.delete(ref);
    else if (isShown(data)) data.refetch();
  }
}
